/**
 * LifecyclePhaseEditor — panel central del Lifecycle Designer.
 * (B.6.d.9.e + B.6.d.9.i — modelo "deliverable" 1:1)
 *
 * Renderiza el editor del nodo seleccionado en el árbol. Dos modos
 * según `state.selectedNode.kind`:
 *
 *   - `phase`     → editor de la phase: name + description +
 *                   gate_description (i18n) + default_contract_mode +
 *                   contract_mode_overridable (structural).
 *   - `subphase`  → editor del entregable (sub-fase + artifact en uno):
 *                   nombre + descripción de la sub-fase, plantilla del
 *                   artefacto (read-only), default_count, is_optional,
 *                   user_can_add_more.
 *
 * El kind `expected_artifact` queda como compat con el reducer pero ya
 * no se selecciona desde la UI — tree colapsó sub-fase y artifact en
 * un solo nodo `subphase`.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'

import {
  listTemplates,
  type LifecycleContractMode,
  type Template,
} from '../../../api/templates'
import { sortedCitable } from '../../../lib/methodologyScope'
import { useAccessToken } from '../../../auth/useAccessToken'
import {
  findExpectedArtifact,
  findPhase,
  findPhaseI18n,
  findSubphase,
  findSubphaseI18n,
  type LifecycleDesignerAction,
  type LifecycleDesignerState,
} from './lifecycleState'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '../../../i18n/config'

interface LifecyclePhaseEditorProps {
  state: LifecycleDesignerState
  dispatch: React.Dispatch<LifecycleDesignerAction>
  readOnly: boolean
  /** Metodología del ciclo (OT-0055): roles y flujos que se ofrecen son
   * los suyos y los genéricos (`citableTemplates`). */
  methodologyId: string | null
}

export function LifecyclePhaseEditor({
  state,
  dispatch,
  readOnly,
  methodologyId,
}: LifecyclePhaseEditorProps) {
  const { t } = useTranslation('templates')

  if (!state.selectedNode) {
    return (
      <div className="h-full flex items-center justify-center p-12">
        <div className="text-center max-w-md">
          <h3 className="font-display text-lg text-ink mb-2">
            {t('designer.lifecycle.editor_no_selection_title')}
          </h3>
          <p className="font-body text-sm text-ink-muted">
            {t('designer.lifecycle.editor_no_selection_body')}
          </p>
        </div>
      </div>
    )
  }

  const node = state.selectedNode
  if (node.kind === 'phase') {
    return (
      <PhaseEditor
        state={state}
        dispatch={dispatch}
        readOnly={readOnly}
        methodologyId={methodologyId}
      />
    )
  }
  // `subphase` y, por compat, `expected_artifact` (el reducer todavía
  // soporta ese kind si el legacy llega a seleccionarlo): deliverable.
  return (
    <DeliverableEditor
      state={state}
      dispatch={dispatch}
      readOnly={readOnly}
      methodologyId={methodologyId}
    />
  )
}

// ─── Phase editor ──────────────────────────────────────────────────────────
function PhaseEditor({
  state,
  dispatch,
  readOnly,
}: LifecyclePhaseEditorProps) {
  const { t } = useTranslation('templates')
  // Cuando el user intenta cambiar el contract_mode a uno incompatible
  // con los deliverables actuales, guardamos el target y mostramos un
  // confirm dialog. Si confirma, limpiamos los deliverables + aplicamos
  // el cambio. (B.6.d.9.k)
  const [pendingMode, setPendingMode] =
    useState<LifecycleContractMode | null>(null)
  const node = state.selectedNode
  if (!node || node.kind !== 'phase') return null
  const phase = findPhase(state.definition, node.phaseCode)
  if (!phase) return null
  const deliverableCount = phase.subphases?.length ?? 0

  function tryChangeMode(newMode: LifecycleContractMode) {
    if (!phase) return
    if (newMode === phase.default_contract_mode) return
    if (deliverableCount === 0) {
      // Sin deliverables → cambio directo, sin diálogo.
      dispatch({
        type: 'UPDATE_PHASE_STRUCTURAL',
        phaseCode: phase.phase_code,
        patch: { default_contract_mode: newMode },
      })
      return
    }
    // Con deliverables → confirmar antes de borrar.
    setPendingMode(newMode)
  }

  function applyPendingMode() {
    if (!phase || !pendingMode) return
    dispatch({
      type: 'CLEAR_PHASE_DELIVERABLES',
      phaseCode: phase.phase_code,
    })
    dispatch({
      type: 'UPDATE_PHASE_STRUCTURAL',
      phaseCode: phase.phase_code,
      patch: { default_contract_mode: pendingMode },
    })
    setPendingMode(null)
  }
  const i18n = findPhaseI18n(state.definition, node.phaseCode, state.activeLocale)

  return (
    <div className="h-full overflow-y-auto p-6">
      <header className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-brand-soft text-brand">
            {t('designer.lifecycle.kind_phase')}
          </span>
          <h2 className="font-display text-xl font-semibold text-ink truncate">
            {i18n?.name || phase.phase_code}
          </h2>
        </div>
        {!readOnly ? (
          <button
            type="button"
            onClick={() =>
              dispatch({ type: 'REMOVE_PHASE', phaseCode: phase.phase_code })
            }
            className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-critical px-2 py-1 rounded-md hover:bg-critical-soft transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t('designer.lifecycle.remove_phase')}
          </button>
        ) : null}
      </header>

      <LocaleTabs state={state} dispatch={dispatch} />

      <div className="mt-4 space-y-4">
        <Field label={t('designer.lifecycle.phase_name_label')}>
          <input
            type="text"
            value={i18n?.name ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_PHASE_I18N',
                phaseCode: phase.phase_code,
                locale: state.activeLocale,
                patch: { name: e.target.value },
              })
            }
            disabled={readOnly}
            placeholder={t('designer.lifecycle.phase_name_placeholder')}
            className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
          />
        </Field>

        <Field
          label={t('designer.lifecycle.phase_code_label')}
          help={t('designer.lifecycle.phase_code_help')}
        >
          <input
            type="text"
            value={phase.phase_code}
            disabled
            className="w-full bg-surface-muted border border-line rounded-md px-3 py-2 text-xs font-mono text-ink-subtle disabled:opacity-100"
          />
        </Field>

        <Field
          label={t('designer.lifecycle.phase_description_label')}
          help={t('designer.lifecycle.phase_description_help')}
        >
          <textarea
            value={i18n?.description ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_PHASE_I18N',
                phaseCode: phase.phase_code,
                locale: state.activeLocale,
                patch: { description: e.target.value },
              })
            }
            disabled={readOnly}
            rows={3}
            placeholder={t('designer.lifecycle.phase_description_placeholder')}
            className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body leading-relaxed focus:outline-none focus:border-brand resize-y disabled:opacity-60"
          />
        </Field>

        <Field
          label={t('designer.lifecycle.gate_description_label')}
          help={t('designer.lifecycle.gate_description_help')}
        >
          <textarea
            value={i18n?.gate_description ?? phase.gate_description ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_PHASE_I18N',
                phaseCode: phase.phase_code,
                locale: state.activeLocale,
                patch: { gate_description: e.target.value },
              })
            }
            disabled={readOnly}
            rows={3}
            placeholder={t('designer.lifecycle.gate_description_placeholder')}
            className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body leading-relaxed focus:outline-none focus:border-brand resize-y disabled:opacity-60"
          />
        </Field>
      </div>

      <div className="mt-8 pt-5 border-t border-line space-y-4">
        <Field
          label={t('designer.lifecycle.contract_mode_label')}
          help={t('designer.lifecycle.contract_mode_help')}
        >
          <div className="flex flex-wrap gap-2">
            {(['internal', 'skipped'] as LifecycleContractMode[]).map(
              (mode) => {
                const checked = phase.default_contract_mode === mode
                return (
                  <label
                    key={mode}
                    className={
                      checked
                        ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-brand bg-brand-soft/40 text-sm cursor-pointer'
                        : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-line hover:bg-surface-muted text-sm cursor-pointer'
                    }
                  >
                    <input
                      type="radio"
                      name={`contract_mode_${phase.phase_code}`}
                      checked={checked}
                      onChange={() => tryChangeMode(mode)}
                      disabled={readOnly}
                      className="accent-brand"
                    />
                    <span>
                      {t(`designer.lifecycle.contract_mode_${mode}`)}
                    </span>
                  </label>
                )
              },
            )}
          </div>
        </Field>

        <ToggleField
          label={t('designer.lifecycle.contract_overridable_label')}
          help={t('designer.lifecycle.contract_overridable_help')}
          checked={phase.contract_mode_overridable === true}
          disabled={readOnly}
          onChange={(checked) =>
            dispatch({
              type: 'UPDATE_PHASE_STRUCTURAL',
              phaseCode: phase.phase_code,
              patch: { contract_mode_overridable: checked },
            })
          }
        />
      </div>

      {/* Confirm dialog para cambio de modo con deliverables existentes */}
      {pendingMode ? (
        <ConfirmModeChangeDialog
          fromMode={phase.default_contract_mode}
          toMode={pendingMode}
          deliverableCount={deliverableCount}
          onCancel={() => setPendingMode(null)}
          onConfirm={applyPendingMode}
        />
      ) : null}
    </div>
  )
}

// ─── Confirm dialog para cambio de phase mode (B.6.d.9.k) ─────────────────
function ConfirmModeChangeDialog({
  fromMode,
  toMode,
  deliverableCount,
  onCancel,
  onConfirm,
}: {
  fromMode: LifecycleContractMode
  toMode: LifecycleContractMode
  deliverableCount: number
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation('templates')
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div className="w-full max-w-md rounded-md border border-line bg-surface shadow-lg">
        <header className="px-5 pt-4 pb-3 border-b border-line">
          <h2 className="font-display text-lg font-semibold text-ink">
            {t('designer.lifecycle.mode_change_confirm_title')}
          </h2>
        </header>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-ink leading-relaxed">
            {t('designer.lifecycle.mode_change_confirm_body', {
              from: t(`designer.lifecycle.contract_mode_${fromMode}`),
              to: t(`designer.lifecycle.contract_mode_${toMode}`),
              count: deliverableCount,
            })}
          </p>
          <p className="text-xs text-ink-muted leading-relaxed">
            {t('designer.lifecycle.mode_change_confirm_hint')}
          </p>
        </div>
        <footer className="px-5 py-3 border-t border-line flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md"
          >
            {t('designer.picker.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-5 py-2 bg-critical hover:bg-critical/90 text-canvas text-sm font-medium rounded-md"
          >
            {t('designer.lifecycle.mode_change_confirm_button')}
          </button>
        </footer>
      </div>
    </div>
  )
}

// ─── Deliverable editor (sub-fase + artifact fundidos) ────────────────────
function DeliverableEditor({
  state,
  dispatch,
  readOnly,
  methodologyId,
}: LifecyclePhaseEditorProps) {
  const { t, i18n: i18nLib } = useTranslation('templates')
  const { accessToken } = useAccessToken()
  // Catálogo de artifact templates del tenant — reusa la cache del
  // LifecycleArtifactPickerDialog (mismo queryKey base) para resolver
  // el `template_key` técnico al `name` legible del template author.
  const { data: artifactTemplatesCatalog = [] } = useQuery({
    queryKey: ['templates', 'list', 'artifact', ''],
    queryFn: () =>
      listTemplates(accessToken!, { template_type: 'artifact' }),
    enabled: Boolean(accessToken),
    staleTime: 30_000,
  })

  // Catálogo de roles para el dropdown.
  const { data: roles = [] } = useQuery({
    queryKey: ['templates', 'list', 'role'],
    queryFn: () =>
      listTemplates(accessToken!, { template_type: 'role' }),
    enabled: Boolean(accessToken),
    staleTime: 60_000,
  })
  const sortedRoles = useMemo(
    () => sortedCitable(roles, methodologyId, i18nLib.language || DEFAULT_LOCALE),
    [roles, methodologyId, i18nLib.language],
  )

  // Doc 21 Fase 3 — catálogo de approval workflow templates para el
  // dropdown que asigna un workflow a esta sub-fase.
  const { data: workflows = [] } = useQuery({
    queryKey: ['templates', 'list', 'approval_workflow'],
    queryFn: () =>
      listTemplates(accessToken!, { template_type: 'approval_workflow' }),
    enabled: Boolean(accessToken),
    staleTime: 60_000,
  })
  const sortedWorkflows = useMemo(
    () => sortedCitable(workflows, methodologyId, i18nLib.language || DEFAULT_LOCALE),
    [workflows, methodologyId, i18nLib.language],
  )

  const node = state.selectedNode
  if (!node || node.kind !== 'subphase') return null

  const subphase = findSubphase(
    state.definition,
    node.phaseCode,
    node.subphaseCode,
  )
  if (!subphase) return null
  const phaseI18n = findPhaseI18n(
    state.definition,
    node.phaseCode,
    state.activeLocale,
  )
  const i18n = findSubphaseI18n(
    state.definition,
    node.phaseCode,
    node.subphaseCode,
    state.activeLocale,
  )

  // Buscar el artifact 1:1 ligado a esta sub-fase. En el modelo nuevo
  // siempre hay uno; si no lo hay (legacy / state inconsistente),
  // mostramos un warning + permitimos eliminar.
  const phase = findPhase(state.definition, node.phaseCode)
  const art = phase?.expected_artifacts?.find(
    (a) => a.subphase_code === node.subphaseCode,
  )

  // Detectar si la subphase tiene un override embebido — en ese caso, el
  // selector de template_key queda deshabilitado con un aviso (precedencia:
  // override > template, lo decide el resolver del backend, Doc 21 D2).
  const hasEmbeddedOverride =
    typeof subphase['approval_workflow_override'] === 'object' &&
    subphase['approval_workflow_override'] !== null
  const currentWorkflowKey =
    typeof subphase['approval_workflow_template_key'] === 'string'
      ? (subphase['approval_workflow_template_key'] as string)
      : ''

  return (
    <div className="h-full overflow-y-auto p-6">
      <header className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-official-soft text-official">
            {t('designer.lifecycle.kind_deliverable')}
          </span>
          <h2 className="font-display text-xl font-semibold text-ink truncate">
            {i18n?.name || subphase.subphase_code}
          </h2>
        </div>
        {!readOnly ? (
          <button
            type="button"
            onClick={() =>
              dispatch({
                type: 'REMOVE_DELIVERABLE',
                phaseCode: node.phaseCode,
                subphaseCode: subphase.subphase_code,
              })
            }
            className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-critical px-2 py-1 rounded-md hover:bg-critical-soft transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {t('designer.lifecycle.remove_deliverable')}
          </button>
        ) : null}
      </header>

      <p className="text-xs text-ink-subtle mb-4">
        {t('designer.lifecycle.subphase_parent_label', {
          phase: phaseI18n?.name || node.phaseCode,
        })}
      </p>

      <LocaleTabs state={state} dispatch={dispatch} />

      {/* ─── Sub-fase i18n ─────────────────────────────────────────── */}
      <div className="mt-4 space-y-4">
        <Field label={t('designer.lifecycle.deliverable_subphase_name_label')}>
          <input
            type="text"
            value={i18n?.name ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_SUBPHASE_I18N',
                phaseCode: node.phaseCode,
                subphaseCode: subphase.subphase_code,
                locale: state.activeLocale,
                patch: { name: e.target.value },
              })
            }
            disabled={readOnly}
            placeholder={t(
              'designer.lifecycle.deliverable_subphase_name_placeholder',
            )}
            className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
          />
        </Field>

        <Field
          label={t('designer.lifecycle.subphase_code_label')}
          help={t('designer.lifecycle.subphase_code_help')}
        >
          <input
            type="text"
            value={subphase.subphase_code}
            disabled
            className="w-full bg-surface-muted border border-line rounded-md px-3 py-2 text-xs font-mono text-ink-subtle disabled:opacity-100"
          />
        </Field>

        <Field
          label={t('designer.lifecycle.subphase_description_label')}
          help={t('designer.lifecycle.subphase_description_help')}
        >
          <textarea
            value={i18n?.description ?? ''}
            onChange={(e) =>
              dispatch({
                type: 'UPDATE_SUBPHASE_I18N',
                phaseCode: node.phaseCode,
                subphaseCode: subphase.subphase_code,
                locale: state.activeLocale,
                patch: { description: e.target.value },
              })
            }
            disabled={readOnly}
            rows={3}
            placeholder={t(
              'designer.lifecycle.subphase_description_placeholder',
            )}
            className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-body leading-relaxed focus:outline-none focus:border-brand resize-y disabled:opacity-60"
          />
        </Field>
      </div>

      {/* ─── Artefacto vinculado + configuración ──────────────────── */}
      <div className="mt-8 pt-5 border-t border-line space-y-4">
        <h3 className="font-display text-sm font-semibold text-ink">
          {t('designer.lifecycle.deliverable_section_artifact_title')}
        </h3>

        {!art ? (
          <p className="rounded-md border border-critical/40 bg-critical-soft p-3 text-xs text-critical">
            {t('designer.lifecycle.deliverable_no_artifact')}
          </p>
        ) : (
          <>
            {art.template_key ? (
              // Template-based (phase mode 'internal').
              // Resolve template_key → name humano del catálogo. Fallback
              // al key si el catálogo aún no cargó o si el template fue
              // archivado/borrado del catálogo (caso edge).
              (() => {
                const matched = artifactTemplatesCatalog.find(
                  (tpl) => tpl.template_key === art.template_key,
                )
                const displayName = matched?.name ?? art.template_key
                return (
                  <Field
                    label={t('designer.lifecycle.artifact_template_key_label')}
                    help={t('designer.lifecycle.artifact_template_key_help')}
                  >
                    <div className="w-full bg-surface-muted border border-line rounded-md px-3 py-2 text-sm">
                      <p className="text-ink">{displayName}</p>
                      <p className="mt-0.5 font-mono text-[10px] text-ink-subtle">
                        {art.template_key}
                      </p>
                    </div>
                  </Field>
                )
              })()
            ) : (
              // OT-0007 — legacy: deliverables sin template_key solo pueden
              // venir de lifecycles publicados antes de esta OT (cuando la
              // fase estaba en modo 'external_canon'). El Designer ya no
              // permite crear uno nuevo así — display-only.
              <div className="rounded-md border border-line bg-surface-muted/40 p-3">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
                  {t('designer.lifecycle.deliverable_external_kind_label')}
                </p>
                <p className="mt-1 text-sm text-ink">
                  {t('designer.lifecycle.deliverable_external_kind_body')}
                </p>
              </div>
            )}

            <Field
              label={
                art.template_key
                  ? t('designer.lifecycle.artifact_default_count_label')
                  : t('designer.lifecycle.artifact_default_count_files_label')
              }
              help={
                art.template_key
                  ? t('designer.lifecycle.artifact_default_count_help')
                  : t('designer.lifecycle.artifact_default_count_files_help')
              }
            >
              <input
                type="number"
                min={1}
                max={20}
                value={art.default_count ?? 1}
                onChange={(e) => {
                  const raw = e.target.value
                  const n = raw.trim() ? Number(raw) : 1
                  dispatch({
                    type: 'UPDATE_EXPECTED_ARTIFACT',
                    phaseCode: node.phaseCode,
                    subphaseCode: art.subphase_code,
                    patch: {
                      default_count:
                        Number.isFinite(n) && n >= 1 ? n : 1,
                    },
                  })
                }}
                disabled={readOnly}
                className="w-32 bg-canvas border border-line rounded-md px-3 py-2 text-sm font-mono"
              />
            </Field>

            <ToggleField
              label={t('designer.lifecycle.artifact_is_optional_label')}
              help={t('designer.lifecycle.artifact_is_optional_help')}
              checked={art.is_optional === true}
              disabled={readOnly}
              onChange={(checked) =>
                dispatch({
                  type: 'UPDATE_EXPECTED_ARTIFACT',
                  phaseCode: node.phaseCode,
                  subphaseCode: art.subphase_code,
                  patch: { is_optional: checked },
                })
              }
            />

            <ToggleField
              label={
                art.template_key
                  ? t('designer.lifecycle.artifact_user_can_add_more_label')
                  : t('designer.lifecycle.artifact_user_can_upload_more_label')
              }
              help={
                art.template_key
                  ? t('designer.lifecycle.artifact_user_can_add_more_help')
                  : t('designer.lifecycle.artifact_user_can_upload_more_help')
              }
              checked={art.user_can_add_more === true}
              disabled={readOnly}
              onChange={(checked) =>
                dispatch({
                  type: 'UPDATE_EXPECTED_ARTIFACT',
                  phaseCode: node.phaseCode,
                  subphaseCode: art.subphase_code,
                  patch: { user_can_add_more: checked },
                })
              }
            />

            <Field
              label={t('designer.lifecycle.deliverable_role_label')}
              help={t('designer.lifecycle.deliverable_role_help')}
            >
              <select
                value={art.role_template_key ?? ''}
                onChange={(e) =>
                  dispatch({
                    type: 'UPDATE_EXPECTED_ARTIFACT',
                    phaseCode: node.phaseCode,
                    subphaseCode: art.subphase_code,
                    patch: {
                      role_template_key: e.target.value || null,
                    },
                  })
                }
                disabled={readOnly}
                className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
              >
                <option value="">
                  — {t('designer.lifecycle.deliverable_role_unassigned')} —
                </option>
                {sortedRoles.map((role) => (
                  <option key={role.id} value={role.template_key}>
                    {role.name}
                  </option>
                ))}
                <NotCitableOption
                  value={art.role_template_key}
                  options={sortedRoles}
                  all={roles}
                />
              </select>
            </Field>
          </>
        )}
      </div>

      {/* ─── Approval workflow (Doc 21 — Fase 3) ──────────────────── */}
      <div className="mt-8 pt-5 border-t border-line space-y-4">
        <header>
          <h3 className="font-display text-sm font-semibold text-ink">
            {t('designer.lifecycle.approval_workflow_section_title')}
          </h3>
          <p className="mt-1 text-xs text-ink-muted">
            {t('designer.lifecycle.approval_workflow_section_help')}
          </p>
        </header>

        {hasEmbeddedOverride ? (
          <p className="rounded-md border border-review/40 bg-review-soft/40 p-3 text-xs text-ink-muted">
            {t('designer.lifecycle.approval_workflow_override_active')}
          </p>
        ) : (
          <Field
            label={t('designer.lifecycle.approval_workflow_label')}
            help={t('designer.lifecycle.approval_workflow_help')}
          >
            <select
              value={currentWorkflowKey}
              onChange={(e) =>
                dispatch({
                  type: 'UPDATE_SUBPHASE_STRUCTURAL',
                  phaseCode: node.phaseCode,
                  subphaseCode: subphase.subphase_code,
                  patch: {
                    approval_workflow_template_key: e.target.value || null,
                  },
                })
              }
              disabled={readOnly}
              className="w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand disabled:opacity-60"
            >
              <option value="">
                — {t('designer.lifecycle.approval_workflow_unassigned')} —
              </option>
              {sortedWorkflows.map((wf) => (
                <option key={wf.id} value={wf.template_key}>
                  {wf.name}
                </option>
              ))}
              <NotCitableOption
                value={currentWorkflowKey}
                options={sortedWorkflows}
                all={workflows}
              />
            </select>
          </Field>
        )}
      </div>
    </div>
  )
}

// Compat: dejar exportable para legacy. Ya no se monta desde el editor,
// pero el reducer todavía soporta el kind. Si en el futuro se rescata
// (ej. modo avanzado N:1), se pone aquí.
export function _ExpectedArtifactEditorLegacy(): null {
  // El editor fundido cubre el caso. Stub vacío para silenciar el
  // import unused si alguien lo referencia desde fuera.
  void findExpectedArtifact
  return null
}

// ─── Helpers compartidos ──────────────────────────────────────────────────
/** El valor elegido que ya no se ofrece (de otra metodología o archivado):
 * se muestra, marcado, para que el selector no lo pierda en silencio. */
function NotCitableOption({
  value,
  options,
  all,
}: {
  value: string | null | undefined
  options: Template[]
  all: Template[]
}) {
  const { t } = useTranslation('templates')
  if (!value || options.some((o) => o.template_key === value)) return null
  const name = all.find((tpl) => tpl.template_key === value)?.name ?? value
  return <option value={value}>{t('designer.lifecycle.not_citable_option', { name })}</option>
}

function LocaleTabs({
  state,
  dispatch,
}: {
  state: LifecycleDesignerState
  dispatch: React.Dispatch<LifecycleDesignerAction>
}) {
  const { t } = useTranslation('templates')
  const allLocales = SUPPORTED_LOCALES
  const availableLocales = Object.keys(state.definition.i18n) as (
    | 'es'
    | 'en'
  )[]
  return (
    <nav className="flex border-b border-line">
      {allLocales.map((loc) => {
        const isActive = state.activeLocale === loc
        const isAvailable = availableLocales.includes(loc)
        return (
          <button
            key={loc}
            type="button"
            onClick={() =>
              isAvailable && dispatch({ type: 'SET_LOCALE', locale: loc })
            }
            disabled={!isAvailable}
            className={
              isActive
                ? 'px-4 py-2 text-xs font-mono uppercase border-b-2 border-brand text-brand'
                : isAvailable
                  ? 'px-4 py-2 text-xs font-mono uppercase border-b-2 border-transparent text-ink-muted hover:text-ink'
                  : 'px-4 py-2 text-xs font-mono uppercase border-b-2 border-transparent text-ink-subtle cursor-not-allowed opacity-50'
            }
          >
            {loc === 'es'
              ? t('designer.editor.tab_es')
              : t('designer.editor.tab_en')}
          </button>
        )
      })}
    </nav>
  )
}

function Field({
  label,
  help,
  children,
}: {
  label: string
  help?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-muted">{label}</span>
      <div className="mt-1">{children}</div>
      {help ? (
        <span className="mt-1 block text-[11px] text-ink-subtle">{help}</span>
      ) : null}
    </label>
  )
}

function ToggleField({
  label,
  help,
  checked,
  disabled,
  onChange,
}: {
  label: string
  help?: string
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="mt-1 accent-brand"
      />
      <div>
        <span className="text-sm text-ink">{label}</span>
        {help ? (
          <span className="block text-[11px] text-ink-subtle">{help}</span>
        ) : null}
      </div>
    </label>
  )
}
